import axios from "axios";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getToken, refreshToken, clearToken } from "@/infrastructure/auth/authService";
import { CONFIG } from "@/config";

const BASE_URL = CONFIG.WAREHOUSE_API_URL;

export const warehouseClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10_000,
  headers: { "Content-Type": "application/json" },
});

warehouseClient.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

warehouseClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(warehouseClient(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshToken();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return warehouseClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearToken();
      window.location.hash = "login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export function extractStatusCode(err: unknown): number {
  if (axios.isAxiosError(err) && err.response) {
    return err.response.status;
  }
  return 0;
}

export function extractResponseMessage(err: unknown): string | null {
  if (axios.isAxiosError(err) && err.response?.data) {
    const data = err.response.data;
    if (typeof data.message === "string") return data.message;
    if (Array.isArray(data.message) && data.message.length > 0) return data.message[0];
  }
  return null;
}
